import { useEffect, useState } from 'react';
import { FormControl, MenuItem, InputLabel, useMediaQuery, useTheme, Select, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { getModelDisplayName, DEFAULT_ENGINE_MODELS } from '../constants/appConstants';

import guruLogo from '../assets/logos/guru-icon.svg';
import openaiLogo from '../assets/logos/chatgpt-icon.svg';
import claudeLogo from '../assets/logos/claude-ai-icon.svg';
import geminiLogo from '../assets/logos/google-gemini-icon.svg';
import deepseekLogo from '../assets/logos/deepseek_logo.svg';
import grokLogo from '../assets/logos/grok.svg';

export default function EngineSelect({ selectedEngine, onEngineChange, disabled }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { settings, updateSettings } = useUserSettings();
  const [displayName, setDisplayName] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    // Get the current model for the selected engine or use the default model
    const currentModel = settings?.engineSettings?.[selectedEngine]?.model || DEFAULT_ENGINE_MODELS[selectedEngine];

    // Update display name when engine or model changes
    if (currentModel) {
      const newDisplayName = getModelDisplayName(selectedEngine, currentModel);
      if (newDisplayName) {
        setDisplayName(newDisplayName);
      } else {
        // Fallback to default engine names
        setDisplayName(t(`engine.${selectedEngine}`));
      }
    } else {
      setDisplayName(t(`engine.${selectedEngine}`));
    }
  }, [selectedEngine, settings.engineSettings, t]);

  const handleChange = async (event) => {
    const newEngine = event.target.value;
    onEngineChange(newEngine);
    await updateSettings({ selectedEngine: newEngine });
  };

  const engines = [
    { key: 'guru', text: t('engine.guru'), icon: guruLogo },
    {
      key: 'chatgpt',
      text: getModelDisplayName('chatgpt', settings?.engineSettings?.chatgpt?.model || DEFAULT_ENGINE_MODELS.chatgpt) || t('engine.chatgpt'),
      icon: openaiLogo
    },
    {
      key: 'claude',
      text: getModelDisplayName('claude', settings?.engineSettings?.claude?.model || DEFAULT_ENGINE_MODELS.claude) || t('engine.claude'),
      icon: claudeLogo
    },
    {
      key: 'gemini',
      text: getModelDisplayName('gemini', settings?.engineSettings?.gemini?.model || DEFAULT_ENGINE_MODELS.gemini) || t('engine.gemini'),
      icon: geminiLogo
    },
    {
      key: 'deepseek',
      text: getModelDisplayName('deepseek', settings?.engineSettings?.deepseek?.model || DEFAULT_ENGINE_MODELS.deepseek) || t('engine.deepseek'),
      icon: deepseekLogo
    },
    {
      key: 'grok',
      text: getModelDisplayName('grok', settings?.engineSettings?.grok?.model || DEFAULT_ENGINE_MODELS.grok) || t('engine.grok'),
      icon: grokLogo
    },
  ];

  return (
    <FormControl variant="outlined" fullWidth margin="dense">
      {!isMobile ? (
        <>
          <InputLabel>{t('engine.selectLabel')}</InputLabel>
          <Select
            label={t('engine.selectLabel')}
            value={selectedEngine}
            onChange={handleChange}
            disabled={disabled}
          >
            {engines.map((engine) => (
              <MenuItem key={engine.key} value={engine.key}>
                {engine.text}
              </MenuItem>
            ))}
          </Select>
        </>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          width: '100%',
          padding: '0'
        }}>
          {engines.map((engine) => (
            <IconButton
              key={`mobile-${engine.key}`}
              onClick={() => {
                onEngineChange(engine.key);
              }
              }
              disabled={disabled}
              sx={{
                width: '100%',
                height: '32px',
                minHeight: '32px',
                padding: '4px',
                borderRadius: '4px',
                border: engine.key === selectedEngine ? '2px solid #1976d2' : '1px solid rgba(255, 255, 255, 0.23)',
                backgroundColor: engine.key === selectedEngine ? theme.palette.action.selected : 'transparent',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0'
              }}
            >
              <img
                src={engine.icon}
                alt={engine.name}
                style={{
                  height: '20px', // Reduced icon size
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
            </IconButton>
          ))}
        </div>
      )}
    </FormControl>
  );
}
